import type { CountryFieldCopy } from '@voll-intelligence/intelligence-ui-kit';
import type { CheckoutFieldCopy } from '@voll-intelligence/intelligence-ui-kit/checkout';
import type { GenUiTranslate } from '../../../../shared/ui/i18n.js';

/* The copy of the checkout fields (`CheckoutFieldCopy`, in the kit) from the
 * genui catalog: one source for the checkout's form and for the host screens
 * that reuse the same fields (`checkoutCopy(locale).fields`). */

function countryCopy(t: GenUiTranslate, label: string): CountryFieldCopy {
  return {
    label,
    placeholder: t('productCheckout.field.nationalityPlaceholder'),
    searchPlaceholder: t('productCheckout.field.countrySearch'),
    empty: t('productCheckout.field.countryEmpty'),
    unavailable: t('productCheckout.field.countryUnavailable'),
    outsideCatalog: t('productCheckout.field.countryOutsideCatalog'),
    retry: t('productCheckout.field.countryRetry'),
  };
}

export function checkoutFieldCopy(t: GenUiTranslate): CheckoutFieldCopy {
  const editInProfileHint = t('productCheckout.hint.editInProfile');
  return {
    birthdate: { label: t('productCheckout.field.birthdate'), editInProfileHint },
    date: {
      placeholder: t('productCheckout.field.datePick'),
      confirm: t('productCheckout.field.dateConfirm'),
      back: t('productCheckout.field.dateBack'),
      previousMonth: t('productCheckout.field.datePrevMonth'),
      nextMonth: t('productCheckout.field.dateNextMonth'),
    },
    nationality: countryCopy(t, t('productCheckout.field.nationality')),
    document: {
      type: t('productCheckout.field.document'),
      cpf: t('productCheckout.field.cpf'),
      passport: t('productCheckout.field.passport'),
      nationalDoc: t('productCheckout.field.nationalDoc'),
      expiry: t('productCheckout.field.documentExpiry'),
      issuingCountry: countryCopy(t, t('productCheckout.field.issuingCountry')),
      documentOnFile: (value) => t('productCheckout.hint.documentOnFile', { value }),
      editInProfileHint,
      cpfNotOnFileHint: t('productCheckout.hint.cpfNotOnFile'),
      cpfNotOnFileNoAlternativeHint: t('productCheckout.hint.cpfNotOnFileNoAlternative'),
      cpfInvalid: t('productCheckout.hint.cpfInvalid'),
    },
    gender: {
      label: t('productCheckout.field.gender'),
      female: t('productCheckout.field.genderFemale'),
      male: t('productCheckout.field.genderMale'),
      note: t('productCheckout.field.genderNote'),
      why: t('productCheckout.field.genderWhy'),
    },
    embark: {
      nameLabel: t('productCheckout.field.boardingName'),
      surnameLabel: t('productCheckout.field.boardingSurname'),
    },
    phone: { label: t('productCheckout.field.phone') },
    allocation: {
      company: t('productCheckout.fieldCompany'),
      costCenter: t('productCheckout.fieldCostCenter'),
      project: t('productCheckout.fieldProject'),
      projectOptional: t('productCheckout.fieldProjectOptional'),
      selectPlaceholder: t('productCheckout.selectPlaceholder'),
      companySearchEmpty: t('productCheckout.companySearchEmpty'),
      projectSearchEmpty: t('productCheckout.projectSearchEmpty'),
      costCenterSearchPrompt: t('productCheckout.costCenterSearchPrompt'),
      costCenterSearchLoading: t('productCheckout.costCenterSearchLoading'),
      costCenterSearchEmpty: t('productCheckout.costCenterSearchEmpty'),
      noCorporateAccess: t('productCheckout.hint.noCorporateAccess'),
      noCostCenters: t('productCheckout.hint.noCostCenters'),
    },
    allocationSplit: {
      percent: t('productCheckout.split.percent'),
      add: t('productCheckout.split.add'),
      remove: t('productCheckout.split.remove'),
      row: (position) => t('productCheckout.split.row', { position }),
      approvalFlow: t('productCheckout.split.approvalFlow'),
      approvalFlowPhrases: [
        t('productCheckout.split.approvalFlowPhrase1'),
        t('productCheckout.split.approvalFlowPhrase2'),
        t('productCheckout.split.approvalFlowPhrase3'),
      ],
      problems: {
        zero_percent: t('productCheckout.split.zeroPercent'),
        sum: t('productCheckout.split.sum'),
        repeated: t('productCheckout.split.repeated'),
      },
    },
    motive: {
      label: t('productCheckout.fieldMotive'),
      selectPlaceholder: t('productCheckout.selectPlaceholder'),
      searchPlaceholder: t('productCheckout.motiveSearchPlaceholder'),
      searchEmpty: t('productCheckout.motiveSearchEmpty'),
    },
    observation: {
      label: t('productCheckout.field.internalNotes'),
      placeholder: t('productCheckout.field.internalNotesPlaceholder'),
    },
    docMatchNote: t('productCheckout.documentMatchWarning'),
  };
}
